import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowUpRight, CheckCircle2, XCircle } from 'lucide-react';

import { cn } from '@/lib/utils';
import { formatDate } from '@/lib/format';
import { TaskCompleteButton } from '@/features/patient-record/components/panels/TasksPanel/TaskCompleteButton';
import { TaskPriorityBadge } from '@/features/patient-record/components/panels/TasksPanel/TaskPriorityBadge';
import { classifyDueDate } from '@/features/patient-record/components/panels/TasksPanel/taskHelpers';
import type { PatientTaskStatus, TaskInboxItemViewModel } from '@/features/patient-record/types';

interface TaskInboxRowProps {
  readonly task: TaskInboxItemViewModel;
  /** Resolved owning-record name; `null` while the lookup is pending. */
  readonly entityName: string | null;
  /** Opens the task edit modal. Absent when the row can't be edited inline. */
  readonly onSelect?: (() => void) | undefined;
  /**
   * Whether to show the owning-record link on the row. Off in the grouped view,
   * where the record name already heads the group (PTV2-1321).
   */
  readonly showRecordLink?: boolean;
}

/**
 * One row of the My Cases task inbox (PTV2-1236). Left edge completes an open
 * person task inline (reusing the panel's checkbox); the title opens the edit
 * modal; a trailing link jumps to the owning record.
 */
export function TaskInboxRow({
  task,
  entityName,
  onSelect,
  showRecordLink = true,
}: TaskInboxRowProps): ReactElement {
  const { t } = useTranslation();
  const due = task.dueDate;
  const dueStatus = classifyDueDate(due);
  const isClosed = task.status === 'DONE' || task.status === 'CANCELLED';
  const recordPath =
    task.entityGuid === null
      ? null
      : task.entityType === 'family'
        ? `/families/${task.entityGuid}`
        : `/patients/${task.entityGuid}`;
  const recordLabel =
    entityName ??
    (task.entityType === 'family'
      ? t('routes.myCases.inbox.unknownFamily')
      : t('routes.myCases.inbox.unknownPatient'));

  return (
    <div
      className={cn(
        'group/task flex w-full items-start gap-3 px-4 py-3 hover:bg-muted/40 transition-colors focus-within:bg-muted/60',
        isClosed && 'opacity-70',
      )}
    >
      {isClosed ? (
        <ClosedIndicator status={task.status} />
      ) : task.entityType === 'person' && task.entityGuid !== null ? (
        <TaskCompleteButton guid={task.entityGuid} taskId={task.id} className="mt-0.5" />
      ) : (
        <span className="mt-0.5 inline-block h-4 w-4 shrink-0" aria-hidden="true" />
      )}

      <div className="min-w-0 flex-1">
        {onSelect !== undefined ? (
          <button
            type="button"
            onClick={onSelect}
            className="text-left focus-visible:outline-hidden"
            aria-label={t('patient.tasks.openTaskAriaLabel', { title: task.title })}
          >
            <TaskTitle title={task.title} status={task.status} />
          </button>
        ) : (
          <TaskTitle title={task.title} status={task.status} />
        )}
        <div className="mt-1.5 flex items-center gap-2 flex-wrap">
          <TaskPriorityBadge priority={task.priority} />
          {showRecordLink && recordPath !== null && (
            <Link
              to={recordPath}
              className="inline-flex items-center gap-0.5 text-[11px] text-primary hover:underline focus-visible:outline-hidden focus-visible:underline"
            >
              {recordLabel}
              <ArrowUpRight className="h-3 w-3" aria-hidden="true" />
            </Link>
          )}
          {!isClosed && due !== null && (
            <span
              className={cn(
                'text-[10.5px] inline-flex items-center gap-0.5 tabular-nums',
                dueStatus === 'overdue'
                  ? 'text-destructive font-semibold'
                  : dueStatus === 'soon'
                    ? 'text-warning font-semibold'
                    : 'text-muted-foreground',
              )}
            >
              {dueStatus === 'overdue' && <AlertTriangle className="h-3 w-3" aria-hidden="true" />}
              {formatDate(due)}
            </span>
          )}
          {isClosed && (
            <span className="text-[10.5px] text-muted-foreground">
              {t(`patient.tasks.status.${task.status}`)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function TaskTitle({
  title,
  status,
}: {
  readonly title: string;
  readonly status: PatientTaskStatus;
}): ReactElement {
  return (
    <p
      className={cn(
        'text-[13px] font-medium leading-tight break-words',
        status === 'CANCELLED' ? 'text-muted-foreground line-through' : 'text-foreground',
      )}
    >
      {title}
    </p>
  );
}

function ClosedIndicator({ status }: { readonly status: PatientTaskStatus }): ReactElement | null {
  if (status === 'DONE') {
    return <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" aria-hidden="true" />;
  }
  if (status === 'CANCELLED') {
    return <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />;
  }
  return null;
}
